import type { AnchorHTMLAttributes, FC, MouseEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { animate } from 'motion/react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef
} from 'react'

import { useLenis } from '@/components/generals/smooth-scroll'

type Navigate = (href: string) => void

interface PageTransitionProviderProps {
    children: ReactNode
}

interface TransitionLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string
    children: ReactNode
}

const CLOSE_EASE = [0.76, 0, 0.24, 1] as const
const OPEN_EASE = [0.76, 0, 0.24, 1] as const
const MIN_HOLD_MS = 520

const TransitionContext = createContext<Navigate | null>(null)

const prefersReducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches

const pathnameOf = (url: string): string => url.split('#')[0].split('?')[0]

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Between pages a clean curtain: two dark panels slide in from the top and
 * the bottom, the word "Auren" flashes small in the middle while the page
 * changes behind them, then the panels slide away again.
 */
export const PageTransitionProvider: FC<PageTransitionProviderProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const lenis = useLenis()
    const overlayRef = useRef<HTMLDivElement>(null)
    const topRef = useRef<HTMLDivElement>(null)
    const bottomRef = useRef<HTMLDivElement>(null)
    const wordRef = useRef<HTMLSpanElement>(null)
    const busyRef = useRef(false)
    const coveredRef = useRef(false)
    const coveredAtRef = useRef(0)
    const currentPathRef = useRef(pathnameOf(router.asPath))

    const setCurtain = useCallback((closed: number) => {
        if (topRef.current) topRef.current.style.transform = `translate3d(0, ${((closed - 1) * 100).toFixed(3)}%, 0)`
        if (bottomRef.current) bottomRef.current.style.transform = `translate3d(0, ${((1 - closed) * 100).toFixed(3)}%, 0)`
    }, [])

    const flashWord = useCallback(() => {
        const word = wordRef.current

        if (!word) return

        animate(word, { opacity: [0, 1, 1, 0], scale: [0.94, 1, 1, 1.03] }, { duration: 0.8, times: [0, 0.3, 0.7, 1], ease: 'easeOut' })
    }, [])

    const close = useCallback(async (instant: boolean) => {
        const overlay = overlayRef.current

        if (!overlay) return

        setCurtain(instant ? 1 : 0)
        overlay.style.visibility = 'visible'
        overlay.style.pointerEvents = 'auto'

        if (!instant) {
            await animate(0, 1, {
                duration: 0.75,
                ease: CLOSE_EASE,
                onUpdate: setCurtain
            })
        }

        coveredRef.current = true
        coveredAtRef.current = performance.now()
        flashWord()
    }, [setCurtain, flashWord])

    const open = useCallback(async () => {
        const overlay = overlayRef.current

        if (!overlay) return

        await wait(Math.max(0, MIN_HOLD_MS - (performance.now() - coveredAtRef.current)))

        await animate(1, 0, {
            duration: 0.9,
            ease: OPEN_EASE,
            onUpdate: setCurtain
        })

        overlay.style.visibility = 'hidden'
        overlay.style.pointerEvents = 'none'
        coveredRef.current = false
        busyRef.current = false
    }, [setCurtain])

    const navigate = useCallback<Navigate>(href => {
        if (busyRef.current) return

        if (prefersReducedMotion()) {
            router.push(href)
            return
        }

        busyRef.current = true

        close(false).then(() => {
            router.push(href).then(changed => {
                if (!changed) open()
            }).catch(() => open())
        })
    }, [router, close, open])

    useEffect(() => {
        const handleStart = (url: string, options?: { shallow?: boolean }) => {
            if (options?.shallow) return
            if (pathnameOf(url) === currentPathRef.current) return
            if (busyRef.current || prefersReducedMotion()) return

            busyRef.current = true
            close(true)
        }

        const handleComplete = (url: string, options?: { shallow?: boolean }) => {
            const nextPath = pathnameOf(url)
            const changedPage = nextPath !== currentPathRef.current

            currentPathRef.current = nextPath

            if (options?.shallow || !changedPage) return

            if (!url.includes('#')) lenis?.scrollTo(0, { immediate: true, force: true })

            if (coveredRef.current || busyRef.current) open()
        }

        const handleError = () => {
            if (busyRef.current) open()
        }

        router.events.on('routeChangeStart', handleStart)
        router.events.on('routeChangeComplete', handleComplete)
        router.events.on('routeChangeError', handleError)

        return () => {
            router.events.off('routeChangeStart', handleStart)
            router.events.off('routeChangeComplete', handleComplete)
            router.events.off('routeChangeError', handleError)
        }
    }, [router.events, close, open, lenis])

    return (
        <TransitionContext.Provider value={navigate}>
            {children}

            <div
                ref={overlayRef}
                aria-hidden='true'
                className='fixed inset-0 z-[100] overflow-hidden'
                style={{ visibility: 'hidden', pointerEvents: 'none' }}
            >
                <div
                    ref={topRef}
                    className='absolute inset-x-0 top-0 h-[calc(50%_+_1px)] bg-background shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)] will-change-transform'
                    style={{ transform: 'translate3d(0, -100%, 0)' }}
                />

                <div
                    ref={bottomRef}
                    className='absolute inset-x-0 bottom-0 h-[calc(50%_+_1px)] bg-background shadow-[0_-30px_80px_-20px_rgba(0,0,0,0.85)] will-change-transform'
                    style={{ transform: 'translate3d(0, 100%, 0)' }}
                />

                <span
                    ref={wordRef}
                    className='absolute left-1/2 top-1/2 -ml-[2em] -mt-[0.5em] w-[4em] whitespace-nowrap text-center text-sm leading-none tracking-[-0.02em] text-foreground opacity-0'
                >
                    Auren
                </span>
            </div>
        </TransitionContext.Provider>
    )
}

export const usePageTransition = (): Navigate | null => useContext(TransitionContext)

/**
 * Internal link that plays the curtain before changing page.
 * Modified clicks, new tabs and same-page links keep their normal behaviour.
 */
export const TransitionLink: FC<TransitionLinkProps> = ({ href, onClick, children, ...rest }): ReactNode => {
    const navigate = usePageTransition()
    const lenis = useLenis()

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event)

        if (event.defaultPrevented || !navigate) return
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        if (rest.target && rest.target !== '_self') return

        const url = new URL(href, window.location.href)

        if (url.origin !== window.location.origin) return

        if (url.pathname === window.location.pathname) {
            if (url.hash || url.search !== window.location.search) return

            event.preventDefault()

            if (lenis) lenis.scrollTo(0, { duration: 1.4 })
            else window.scrollTo({ top: 0, behavior: 'smooth' })

            return
        }

        event.preventDefault()
        navigate(`${url.pathname}${url.search}${url.hash}`)
    }

    return (
        <Link href={href} onClick={handleClick} {...rest}>
            {children}
        </Link>
    )
}
